import {updateUrl} from '@github-ui/history'
import {useHovercardAttributesForIssue} from '@github-ui/hovercards/issue'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLBox} from '@github-ui/safe-html/VerifiedHTML'
import {Link} from '@primer/react'
import {useCallback} from 'react'
import {graphql, useFragment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import type {StickyHeaderTitle$key} from './__generated__/StickyHeaderTitle.graphql'
import styles from './StickyHeaderTitle.module.css'

type StickyHeaderTitleProps = {
  headerTitleData: StickyHeaderTitle$key
  scrollToTopOnClick?: boolean
}

export function StickyHeaderTitle({headerTitleData, scrollToTopOnClick}: StickyHeaderTitleProps) {
  const {titleHTML, number, url, repository} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment StickyHeaderTitle on Issue {
        titleHTML
        number
        url
        repository {
          name
          owner {
            login
          }
        }
      }
    `,
    headerTitleData,
  )

  const scrollToTop = useCallback(() => {
    if (!scrollToTopOnClick) {
      return
    }
    setTimeout(() => {
      // Clicking the link to #top will scroll to the top of the page, but it will also append "#top" to the URL.
      // We want to prevent that from happening, so we replace the URL right after the scroll happens.
      updateUrl(url)
    }, 10)
  }, [scrollToTopOnClick, url])

  const issueHovercardAttrs = useHovercardAttributesForIssue({
    owner: repository.owner.login,
    repo: repository.name,
    issueNumber: number,
  })
  const hovercardAttrs = scrollToTopOnClick ? {} : issueHovercardAttrs

  return (
    <div className={styles.stickyTitleContainer}>
      <Link
        {...hovercardAttrs}
        href={scrollToTopOnClick ? '#top' : url}
        target={scrollToTopOnClick ? undefined : '_blank'}
        onClick={scrollToTop}
        className={styles.stickyTitleLink}
      >
        <VerifiedHTMLBox
          as="bdi"
          className="markdown-title"
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string */
          html={titleHTML as SafeHTMLString}
          data-testid={TEST_IDS.issueTitleSticky}
        />
      </Link>
      <span className={styles.issueNumberText}>{LABELS.issueNumber(number)}</span>
    </div>
  )
}
